import type { ComponentPropsWithRef, ElementType, ReactElement } from 'react';

import { SideNavigationTooltip } from './SideNavigationTooltip';
import { navChildRowClassName, navRowLabelClassName } from './side-navigation-classes';

type SideNavigationChildRowOwnProps<E extends ElementType> = {
    readonly active?: boolean;
    readonly as?: E;
    readonly expanded: boolean;
    readonly label: string;
};

export type SideNavigationChildRowProps<E extends ElementType = 'button'> = SideNavigationChildRowOwnProps<E> &
    Omit<ComponentPropsWithRef<E>, keyof SideNavigationChildRowOwnProps<E> | 'children' | 'className'>;

export const SideNavigationChildRow = <E extends ElementType = 'button'>({
    active = false,
    as,
    expanded,
    label,
    ...props
}: SideNavigationChildRowProps<E>): ReactElement => {
    const Component = as ?? 'button';

    return (
        <SideNavigationTooltip expanded={expanded} label={label}>
            <Component
                aria-current={active ? 'page' : undefined}
                aria-label={label}
                className={navChildRowClassName(active, expanded)}
                {...(as === undefined ? { type: 'button' } : {})}
                {...props}
            >
                <span aria-hidden={true} className="relative flex size-5 shrink-0 items-center justify-center">
                    <span
                        className={expanded ? 'opacity-0' : 'opacity-100'}
                        data-current={active || undefined}
                        data-side-nav-bar=""
                    />
                </span>
                <span className={navRowLabelClassName(expanded, 'child')}>{label}</span>
            </Component>
        </SideNavigationTooltip>
    );
};
